import { fmtDateTime } from '../lib/dates.ts';
import { useState } from 'react';
import { AckPrompt } from './AckPrompt.tsx';
import type { Message } from '../lib/api.ts';
import { Attachments } from './Attachments.tsx';
import { AttachButton } from './AttachButton.tsx';
import { HandoffQR } from './HandoffQR.tsx';

interface Props {
  readonly messages: readonly Message[];
  readonly onOpen: (id: string) => void;
  /** Devices attach; accounts read. */
  readonly canAttach?: boolean;
  /** Only a site confirms it read a notice, so only a device
      is offered the control. */
  readonly canRead?: boolean;
  /** Turn a notice into a tracked request. Sites need a way to push
      back that survives the conversation, without the board becoming
      a feed. */
  readonly onRaise?: (m: Message) => void;
  /** Admins only. Hides a notice from the board without touching the
      receipts that prove it was delivered. */
  readonly onArchive?: (id: string) => void;
  /** Only in the archive view, where the reverse is the action. */
  readonly onRestore?: (id: string) => void;
}

const when = (iso: string): string =>
  fmtDateTime(iso);

const sender = (m: Message): string =>
  m.sender_user_name ?? m.sender_device_name ?? 'System';

/**
 * Opening is an explicit click on the message, never a scroll, hover or
 * intersection observer. On an always-on counter screen anything
 * automatic would mark every message read the instant it rendered and
 * quietly destroy read coverage.
 */
export const MessageList = ({
  messages,
  onOpen,
  canAttach,
  canRead,
  onRaise,
  onArchive,
  onRestore,
}: Props) => {
  // Bumping this refetches a message's attachment strip after upload.
  const [version, setVersion] = useState(0);
  const [acking, setAcking] = useState('');
  const [qrFor, setQrFor] = useState('');
  if (messages.length === 0) {
    return <p className="empty">Nothing here yet.</p>;
  }

  return (
    <ul className="msgList">
      {messages.map((m) => (
        <li
          key={m.id}
          className={[
            'msg',
            m.priority === 'urgent' ? 'urgent' : '',
            // Read items recede rather than vanish: staff need to see
            // what has been dealt with, not lose it.
            m.opened_at ? 'isRead' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div className="msgMeta">
            <span className="mono">
              {/* Needed once several categories share one list, or an
                  IT notice and an HR one look identical. */}
              <span
                className="catTag"
                style={{ background: m.category_color }}
              />
              {m.category_name} &middot; {sender(m)}
            </span>
            {/* Only when a site is not already implied by the
                view: on one site's board it would be on every
                card and tell you nothing. */}
            <span className="siteTag">{m.site_name}</span>
            <span className="mono">
            </span>
            <span className="mono">{when(m.created_at)}</span>
          </div>
          <p>{m.body}</p>

          {/* Without this, an estate-wide notice and an instruction
              aimed at this site alone look identical -- which reads as
              a data leak when two sites show the same text. */}
          {m.sent_to_sites > 1 && (
            <span className="fanOut">
              Also sent to {m.sent_to_sites - 1} other site
              {m.sent_to_sites > 2 ? 's' : ''}
            </span>
          )}
          <Attachments
            key={`${m.id}:${version}:${m.attachment_count ?? 0}`}
            messageId={m.id}
          />

          {/* Above the buttons, not inside them: sharing a flex row
              with the actions made it wrap and push them out of line. */}
          {m.requires_ack && (
            <p className="ackBanner">Acknowledgement required</p>
          )}

          <div className="msgActions">
            {canAttach && (
              <button
                className="iconBtn"
                title="Photo from phone"
                aria-label="Photo from phone"
                onClick={() => setQrFor(qrFor === m.id ? '' : m.id)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <rect x="3" y="7" width="18" height="13" rx="2" />
                  <path d="M9 7 L10.5 4 h3 L15 7" />
                  <circle cx="12" cy="13.5" r="3.2" />
                </svg>
              </button>
            )}
            {canAttach && (
              <AttachButton
                messageId={m.id}
                onUploaded={() => setVersion((v) => v + 1)}
              />
            )}
            {/* "Mark opened" described the database column, not what
                a person is doing. This receipt is the evidence HQ sees,
                so the label has to say what it means to the reader. */}
            {/* Only on messages from HQ. Raising a request about
                your own message is not a thing, and the icon there
                just invites confusion. */}
            {onRaise && !m.sender_device_name && (
              <button
                className="iconBtn"
                title="Raise a ticket about this"
                aria-label="Raise a ticket about this"
                onClick={() => onRaise(m)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 4 L20 19 H4 Z" />
                  <path d="M12 10 v4" />
                  <path d="M12 16.5 v.2" />
                </svg>
              </button>
            )}
            {onRestore && (
              <button
                className="iconBtn"
                title="Put back on the board"
                aria-label="Put back on the board"
                onClick={() => onRestore(m.id)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true"
                     fill="none" stroke="currentColor"
                     strokeWidth="1.8" strokeLinecap="round"
                     strokeLinejoin="round">
                  <path d="M4 10h11a4 4 0 0 1 0 8h-6" />
                  <path d="M8 6l-4 4 4 4" />
                </svg>
              </button>
            )}
            {onArchive && (
              <button
                className="iconBtn"
                title="Hide from the board"
                aria-label="Hide from the board"
                onClick={() => onArchive(m.id)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <rect x="3" y="4" width="18" height="4" rx="1" />
                  <path d="M5 8 v11 a1 1 0 0 0 1 1 h12 a1 1 0 0 0 1-1 V8" />
                  <path d="M10 12 h4" />
                </svg>
              </button>
            )}
            {/* Reading and signing are what a site does. An admin
                opening their own notice is not evidence the site saw
                it, and recording it made the sent list disagree with
                the coverage report. */}
            {!canRead ? null : m.requires_ack && !m.acked_at ? (
              <button
                className="primary"
                onClick={() => setAcking(m.id)}
              >
                Sign for this
              </button>
            ) : m.opened_at ? (
              <span className="readMark">Read {when(m.opened_at)}</span>
            ) : (
              <button className="primary" onClick={() => onOpen(m.id)}>
                I have read this
              </button>
            )}
          </div>

          {acking === m.id && (
            <AckPrompt
              messageId={m.id}
              onDone={() => {
                setAcking('');
                onOpen(m.id);
              }}
              onCancel={() => setAcking('')}
            />
          )}

          {qrFor === m.id && (
            <HandoffQR
              messageId={m.id}
              onClose={() => {
                setQrFor('');
                // Photos may have arrived while the QR was open.
                setVersion((v) => v + 1);
              }}
            />
          )}
        </li>
      ))}
    </ul>
  );
};
